import type { Exercise, Lesson } from '@fluent/types';
import { isPhrasesLessonId } from '../content/course.js';
import { getLesson } from '../content/lessons.js';

/**
 * One word/phrase pair the user has been exposed to inside a completed lesson.
 *
 * Multiple appearances of the same `en` text are collapsed to a single entry —
 * `firstLessonId` is whichever lesson taught it first (lowest lessonId by
 * sort order, which matches curriculum sequence given our naming scheme).
 */
export interface VocabEntry {
  en: string;
  ka: string;
  audio_key?: string;
  firstLessonId: string;
  firstLessonTitle: string;
}

/**
 * Extract every vocabulary pair the user has seen across `completedLessonIds`.
 *
 * Sources we mine, in order of preference:
 *   1. `tap_pairs.pairs[]` — single-word vocabulary (cleanest data)
 *   2. `phrase_intro` — the moment the lesson teaches a new phrase
 *   3. `dialogue.lines[]` — wrap-up conversation lines
 *
 * Output is sorted by first-seen lesson, then alphabetically by English text,
 * so the screen reads like a curriculum-ordered glossary.
 *
 * Main course only: phrases-course lessons (PHRASES.md § 3) are not "words
 * learned" — they are skipped.
 */
export function extractVocab(completedLessonIds: string[]): VocabEntry[] {
  // Sort lesson IDs so the "firstLessonId" assignment is deterministic and
  // mirrors curriculum order (lesson-01 < lesson-02 < ...).
  const sorted = completedLessonIds.filter((id) => !isPhrasesLessonId(id)).sort();
  const seen = new Map<string, VocabEntry>();

  for (const lessonId of sorted) {
    const lesson = getLesson(lessonId);
    if (!lesson) continue;
    for (const ex of lesson.exercises) {
      collectFromExercise(ex, lesson, seen);
    }
  }

  return [...seen.values()].sort((a, b) => {
    if (a.firstLessonId !== b.firstLessonId) {
      return a.firstLessonId < b.firstLessonId ? -1 : 1;
    }
    return a.en.localeCompare(b.en);
  });
}

function collectFromExercise(
  ex: Exercise,
  lesson: Lesson,
  seen: Map<string, VocabEntry>,
): void {
  if (ex.type === 'tap_pairs') {
    for (const p of ex.pairs) {
      addEntry(seen, lesson, { en: p.en, ka: p.ka, audio_key: p.audio_key });
    }
    return;
  }
  if (ex.type === 'phrase_intro') {
    addEntry(seen, lesson, { en: ex.en, ka: ex.ka, audio_key: ex.audio_key });
    return;
  }
  if (ex.type === 'dialogue') {
    for (const line of ex.lines) {
      addEntry(seen, lesson, {
        en: line.en,
        ka: line.ka,
        audio_key: line.audio_key,
      });
    }
    return;
  }
  // Other exercise types (translate_*, choose_correct, listen_*, cloze) are
  // tests rather than first-exposure teaching moments. Skip — the answer is
  // already a phrase the user met via phrase_intro, so we'd just dedupe.
}

function addEntry(
  seen: Map<string, VocabEntry>,
  lesson: Lesson,
  pair: { en: string; ka: string; audio_key?: string },
): void {
  const key = pair.en.trim().toLowerCase();
  if (seen.has(key)) return; // first lesson wins (lessons are processed in sort order)
  seen.set(key, {
    en: pair.en,
    ka: pair.ka,
    audio_key: pair.audio_key,
    firstLessonId: lesson.id,
    firstLessonTitle: lesson.title,
  });
}
